import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

/**
 * RTK Query for the per-user email-template features:
 *   - Custom user-authored templates (CRUD)
 *   - Favorites — markers that the user starred a built-in template
 *
 * The built-in template gallery is hardcoded in EmailTemplateGallery.jsx;
 * this API only manages the per-user overlay on top of it.
 */
export const userEmailTemplateApi = createApi({
  reducerPath: "userEmailTemplateApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["UserEmailTemplate"],
  keepUnusedDataFor: 60,
  refetchOnMountOrArgChange: 30,
  endpoints: (build) => ({
    // GET /email-templates/user → { customs: [...], favoriteIds: [...] }
    getUserEmailTemplates: build.query({
      query: () => ({
        url: "/email-templates/user",
        method: "GET",
      }),
      providesTags: ["UserEmailTemplate"],
    }),

    // POST /email-templates/user
    createUserEmailTemplate: build.mutation({
      query: (data) => ({
        url: "/email-templates/user",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["UserEmailTemplate"],
    }),

    // PUT /email-templates/user/:id
    updateUserEmailTemplate: build.mutation({
      query: ({ id, ...data }) => ({
        url: `/email-templates/user/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["UserEmailTemplate"],
    }),

    // DELETE /email-templates/user/:id
    deleteUserEmailTemplate: build.mutation({
      query: (id) => ({
        url: `/email-templates/user/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["UserEmailTemplate"],
    }),

    // POST /email-templates/user/favorite/:builtInId
    favoriteBuiltInTemplate: build.mutation({
      query: (builtInId) => ({
        url: `/email-templates/user/favorite/${builtInId}`,
        method: "POST",
      }),
      invalidatesTags: ["UserEmailTemplate"],
    }),

    // DELETE /email-templates/user/favorite/:builtInId
    unfavoriteBuiltInTemplate: build.mutation({
      query: (builtInId) => ({
        url: `/email-templates/user/favorite/${builtInId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["UserEmailTemplate"],
    }),
  }),
});

export const {
  useGetUserEmailTemplatesQuery,
  useCreateUserEmailTemplateMutation,
  useUpdateUserEmailTemplateMutation,
  useDeleteUserEmailTemplateMutation,
  useFavoriteBuiltInTemplateMutation,
  useUnfavoriteBuiltInTemplateMutation,
} = userEmailTemplateApi;
